/**
 * Shared media utilities for image dimension extraction and filename handling.
 * Used by both server (Node.js) and client (browser) code.
 */

/**
 * Image dimension result
 */
export interface ImageDimensions {
  width?: number;
  height?: number;
}

/**
 * Extract image dimensions from binary data.
 * Supports PNG, JPEG, GIF, and WebP formats.
 *
 * @param data - Image data as Uint8Array (works with both Buffer and ArrayBuffer)
 * @param mimeType - MIME type of the image
 * @returns Extracted dimensions or empty object if extraction fails
 */
export function getImageDimensions(data: Uint8Array, mimeType: string): ImageDimensions {
  try {
    if (mimeType === 'image/png') {
      return getPngDimensions(data);
    } else if (mimeType === 'image/jpeg') {
      return getJpegDimensions(data);
    } else if (mimeType === 'image/gif') {
      return getGifDimensions(data);
    } else if (mimeType === 'image/webp') {
      return getWebpDimensions(data);
    }
  } catch {
    // Ignore dimension extraction errors
  }
  return {};
}

/**
 * PNG: width at bytes 16-19, height at 20-23 (big-endian)
 * Magic bytes: 89 50 4E 47 0D 0A 1A 0A
 */
function getPngDimensions(data: Uint8Array): ImageDimensions {
  if (data.length < 24) return {};

  // Check PNG signature
  if (data[0] !== 0x89 || data[1] !== 0x50 || data[2] !== 0x4E || data[3] !== 0x47 ||
      data[4] !== 0x0D || data[5] !== 0x0A || data[6] !== 0x1A || data[7] !== 0x0A) {
    return {};
  }

  const width = readUint32BE(data, 16);
  const height = readUint32BE(data, 20);

  return validateDimensions(width, height);
}

/**
 * JPEG: Look for SOF0 (0xFFC0) or SOF2 (0xFFC2) marker
 * Height at offset+5, width at offset+7 (big-endian)
 */
function getJpegDimensions(data: Uint8Array): ImageDimensions {
  if (data.length < 10) return {};

  // Check JPEG signature (0xFFD8)
  if (data[0] !== 0xFF || data[1] !== 0xD8) return {};

  let offset = 2;
  while (offset < data.length - 9) {
    if (data[offset] !== 0xFF) break;

    const marker = data[offset + 1];

    // SOF0 or SOF2 marker contains dimensions
    if (marker === 0xC0 || marker === 0xC2) {
      const height = readUint16BE(data, offset + 5);
      const width = readUint16BE(data, offset + 7);
      return validateDimensions(width, height);
    }

    // Move to next segment
    const segmentLength = readUint16BE(data, offset + 2);
    offset += 2 + segmentLength;
  }

  return {};
}

/**
 * GIF: width at bytes 6-7, height at 8-9 (little-endian)
 * Magic: GIF87a or GIF89a
 */
function getGifDimensions(data: Uint8Array): ImageDimensions {
  if (data.length < 10) return {};

  // Check GIF signature
  if (data[0] !== 0x47 || data[1] !== 0x49 || data[2] !== 0x46) return {};

  const width = readUint16LE(data, 6);
  const height = readUint16LE(data, 8);

  return validateDimensions(width, height);
}

/**
 * WebP: Check for VP8 or VP8L chunk
 * Magic: RIFF....WEBP
 */
function getWebpDimensions(data: Uint8Array): ImageDimensions {
  if (data.length < 30) return {};

  // Check RIFF signature
  if (data[0] !== 0x52 || data[1] !== 0x49 || data[2] !== 0x46 || data[3] !== 0x46) return {};

  // Check WEBP format
  if (data[8] !== 0x57 || data[9] !== 0x45 || data[10] !== 0x42 || data[11] !== 0x50) return {};

  // Read chunk type at offset 12
  const format = String.fromCharCode(data[12], data[13], data[14], data[15]);

  if (format === 'VP8 ') {
    // Lossy WebP
    const width = readUint16LE(data, 26) & 0x3FFF;
    const height = readUint16LE(data, 28) & 0x3FFF;
    return validateDimensions(width, height);
  } else if (format === 'VP8L') {
    // Lossless WebP
    const bits = readUint32LE(data, 21);
    const width = (bits & 0x3FFF) + 1;
    const height = ((bits >> 14) & 0x3FFF) + 1;
    return validateDimensions(width, height);
  }

  return {};
}

/**
 * Validate dimensions are within reasonable bounds.
 */
function validateDimensions(width: number, height: number): ImageDimensions {
  const result: ImageDimensions = {};

  if (Number.isInteger(width) && width > 0 && width <= 65535) {
    result.width = width;
  }
  if (Number.isInteger(height) && height > 0 && height <= 65535) {
    result.height = height;
  }

  return result;
}

// Binary reading helpers (cross-platform, no Buffer dependency)
function readUint16BE(data: Uint8Array, offset: number): number {
  return (data[offset] << 8) | data[offset + 1];
}

function readUint16LE(data: Uint8Array, offset: number): number {
  return data[offset] | (data[offset + 1] << 8);
}

function readUint32BE(data: Uint8Array, offset: number): number {
  return ((data[offset] << 24) | (data[offset + 1] << 16) |
          (data[offset + 2] << 8) | data[offset + 3]) >>> 0;
}

function readUint32LE(data: Uint8Array, offset: number): number {
  return ((data[offset + 3] << 24) | (data[offset + 2] << 16) |
          (data[offset + 1] << 8) | data[offset]) >>> 0;
}

/**
 * Sanitize a filename for safe storage.
 * - Converts to lowercase
 * - Replaces non-alphanumeric characters with hyphens
 * - Removes consecutive hyphens
 * - Truncates base name to 50 characters
 * - Preserves extension
 *
 * @param originalName - Original filename
 * @returns Sanitized filename
 */
export function sanitizeMediaFilename(originalName: string): string {
  const ext = originalName.includes('.')
    ? originalName.substring(originalName.lastIndexOf('.'))
    : '';
  const baseName = originalName.includes('.')
    ? originalName.substring(0, originalName.lastIndexOf('.'))
    : originalName;

  const sanitized = baseName
    .toLowerCase()
    .replace(/[^a-z0-9\-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
    .substring(0, 50);

  return sanitized + ext.toLowerCase();
}

/**
 * Validate that a media filename is safe (no path traversal, valid characters).
 *
 * @param filename - Filename to validate
 * @returns true if filename is valid
 */
export function isValidMediaFilename(filename: string): boolean {
  // Must be non-empty
  if (!filename || typeof filename !== 'string') return false;

  // Maximum length check
  if (filename.length > 255) return false;

  // Allow only alphanumeric, dots, hyphens, underscores
  // Must not start with a dot (hidden files)
  if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/.test(filename)) return false;

  // Must not contain path separators or traversal
  if (filename.includes('/') || filename.includes('\\') || filename.includes('..')) return false;

  return true;
}

/**
 * Generate a unique filename by appending a counter if needed.
 *
 * @param sanitizedName - Already sanitized filename
 * @param existsCheck - Function to check if filename exists
 * @returns Unique filename
 */
export function generateUniqueFilename(
  sanitizedName: string,
  existsCheck: (filename: string) => boolean
): string {
  let filename = sanitizedName;
  let counter = 1;

  while (existsCheck(filename)) {
    const ext = sanitizedName.includes('.')
      ? sanitizedName.substring(sanitizedName.lastIndexOf('.'))
      : '';
    const base = sanitizedName.includes('.')
      ? sanitizedName.substring(0, sanitizedName.lastIndexOf('.'))
      : sanitizedName;
    filename = `${base}-${counter}${ext}`;
    counter++;
  }

  return filename;
}

/**
 * MIME types allowed for media upload with their constraints.
 */
export const ALLOWED_MEDIA_TYPES: Record<string, { maxSize: number; requiresMagicValidation: boolean }> = {
  'image/jpeg': { maxSize: 10 * 1024 * 1024, requiresMagicValidation: true },
  'image/png': { maxSize: 10 * 1024 * 1024, requiresMagicValidation: true },
  'image/gif': { maxSize: 5 * 1024 * 1024, requiresMagicValidation: true },
  'image/webp': { maxSize: 10 * 1024 * 1024, requiresMagicValidation: true },
  'image/svg+xml': { maxSize: 1 * 1024 * 1024, requiresMagicValidation: false },
  'image/bmp': { maxSize: 10 * 1024 * 1024, requiresMagicValidation: true },
  'image/tiff': { maxSize: 20 * 1024 * 1024, requiresMagicValidation: true },
  'application/pdf': { maxSize: 50 * 1024 * 1024, requiresMagicValidation: true },
};

/**
 * Check if a MIME type is allowed.
 */
export function isAllowedMediaType(mimeType: string): boolean {
  return mimeType in ALLOWED_MEDIA_TYPES;
}

/**
 * Magic number signatures for file type validation.
 */
const MAGIC_SIGNATURES: Record<string, number[][]> = {
  'image/jpeg': [[0xFF, 0xD8, 0xFF]],
  'image/png': [[0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]],
  'image/gif': [[0x47, 0x49, 0x46, 0x38, 0x37, 0x61], [0x47, 0x49, 0x46, 0x38, 0x39, 0x61]], // GIF87a, GIF89a
  'image/webp': [[0x52, 0x49, 0x46, 0x46]], // RIFF (need to also check WEBP at offset 8)
  'image/bmp': [[0x42, 0x4D]], // BM
  'image/tiff': [[0x49, 0x49, 0x2A, 0x00], [0x4D, 0x4D, 0x00, 0x2A]], // Little/Big endian TIFF
  'application/pdf': [[0x25, 0x50, 0x44, 0x46]], // %PDF
};

/**
 * Validate that file content matches its declared MIME type using magic numbers.
 *
 * @param data - File content
 * @param mimeType - Declared MIME type
 * @returns true if magic numbers match or type doesn't require validation
 */
export function validateMagicNumber(data: Uint8Array, mimeType: string): boolean {
  const typeConfig = ALLOWED_MEDIA_TYPES[mimeType];
  if (!typeConfig || !typeConfig.requiresMagicValidation) {
    return true; // Skip validation for types that don't require it (e.g., SVG)
  }

  const signatures = MAGIC_SIGNATURES[mimeType];
  if (!signatures) {
    return true; // No signature defined, allow
  }

  for (const signature of signatures) {
    if (data.length < signature.length) continue;

    let matches = true;
    for (let i = 0; i < signature.length; i++) {
      if (data[i] !== signature[i]) {
        matches = false;
        break;
      }
    }

    if (matches) {
      // Special check for WebP: also verify WEBP at offset 8
      if (mimeType === 'image/webp' && data.length >= 12) {
        if (data[8] !== 0x57 || data[9] !== 0x45 || data[10] !== 0x42 || data[11] !== 0x50) {
          continue; // Not actually WebP
        }
      }
      return true;
    }
  }

  return false;
}

/**
 * Basic SVG sanitization - remove potentially dangerous elements and attributes.
 * This is a simple sanitizer; for production, consider using DOMPurify.
 *
 * @param svgContent - SVG content as string
 * @returns Sanitized SVG content
 */
export function sanitizeSvg(svgContent: string): string {
  // Remove script tags and their contents
  let sanitized = svgContent.replace(/<script[\s\S]*?<\/script>/gi, '');

  // Remove on* event handlers
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*[^\s>]+/gi, '');

  // Remove javascript: URLs
  sanitized = sanitized.replace(/javascript\s*:/gi, '');

  // Remove data: URLs with script content (but allow data:image)
  sanitized = sanitized.replace(/data\s*:\s*(?!image\/)[^"'\s>]+/gi, '');

  // Remove foreignObject elements (can contain HTML)
  sanitized = sanitized.replace(/<foreignObject[\s\S]*?<\/foreignObject>/gi, '');

  // Remove use elements with external references (can load external content)
  sanitized = sanitized.replace(/<use[^>]*href\s*=\s*["'][^#][^"']*["'][^>]*>/gi, '');

  // Remove set and animate elements that could execute JavaScript
  sanitized = sanitized.replace(/<set[\s\S]*?(?:\/>|<\/set>)/gi, '');
  sanitized = sanitized.replace(/<animate[\s\S]*?(?:\/>|<\/animate>)/gi, '');

  return sanitized;
}
